import React, {useMemo, useState} from "react";
import {ChevronDown, ChevronRight, Layers, Loader, Package, Search} from "lucide-react";
import {useQuery} from "@tanstack/react-query";
import {useTranslation} from "react-i18next";
import moment from "moment";
import {api} from "../../../config/api";
import secureStorage from "../../../utils/secureStorage";
import AppleSelect from "../../Common/AppleSelect";

const formatCurrency = (value) => {
    if (value === null || value === undefined) return "0.00";
    return parseFloat(value).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
};

// Quantities can be decimal (sold by weight) — no trailing ".00" for whole units.
const formatQty = (value) => {
    const n = Number(value) || 0;
    return Number.isInteger(n) ? String(n) : n.toFixed(2);
};

const SORTS = {
    quantity: (a, b) => b.quantity - a.quantity || b.revenue - a.revenue,
    revenue: (a, b) => b.revenue - a.revenue || b.quantity - a.quantity,
    name: (a, b) => (a.name || "").localeCompare(b.name || ""),
};

// Every product an employee sold over the period (GET /activite/employee/{id}/products),
// listed flat or grouped by category, with the quantity and revenue of each product.
const EmployeeSoldProducts = ({employeeId, dateRange, startTime, endTime, isDarkMode = false}) => {
    const {t} = useTranslation("activite");
    const [groupByCategory, setGroupByCategory] = useState(false);
    const [search, setSearch] = useState("");
    const [sortBy, setSortBy] = useState("quantity");
    const [collapsed, setCollapsed] = useState({});

    const range = dateRange?.[0];
    const startDate = range?.startDate ? moment(range.startDate).format("YYYY-MM-DD") : null;
    const endDate = range?.endDate ? moment(range.endDate).format("YYYY-MM-DD") : null;

    const {data, isLoading, isError, isFetching} = useQuery({
        queryKey: ["employee-sold-products", employeeId, startDate, endDate, startTime, endTime],
        queryFn: async () => {
            const account = await secureStorage.getItem("selectedAccount");
            const params = new URLSearchParams();
            if (startDate) params.set("startDate", startDate);
            if (endDate) params.set("endDate", endDate);
            if (startTime) params.set("startTime", startTime);
            if (endTime) params.set("endTime", endTime);
            const response = await api.get(`/activite/employee/${employeeId}/products?${params.toString()}`, {
                headers: {"X-account-id": account.id, Accept: "application/json"},
            });
            return response.data;
        },
        enabled: !!employeeId,
    });

    const products = useMemo(() => {
        const term = search.trim().toLowerCase();
        return (data?.products || [])
            .map((p) => ({...p, name: p.is_free_sale ? t("employeeDetails.freeSaleProduct") : p.name}))
            .filter((p) => !term
                || (p.name || "").toLowerCase().includes(term)
                || (p.category_name || "").toLowerCase().includes(term))
            .sort(SORTS[sortBy]);
    }, [data, search, sortBy, t]);

    // Categories rebuilt from the filtered products so the search applies to both views.
    const categories = useMemo(() => {
        const byKey = new Map();
        for (const p of products) {
            const key = p.category_id || "none";
            if (!byKey.has(key)) {
                byKey.set(key, {key, name: p.category_name, quantity: 0, revenue: 0, products: []});
            }
            const cat = byKey.get(key);
            cat.quantity += p.quantity;
            cat.revenue += p.revenue;
            cat.products.push(p);
        }
        return [...byKey.values()].sort(SORTS[sortBy]);
    }, [products, sortBy]);

    const totals = data?.totals;
    const card = isDarkMode ? "bg-gray-800" : "bg-white";
    const muted = isDarkMode ? "text-gray-400" : "text-gray-500";
    const strong = isDarkMode ? "text-gray-100" : "text-gray-900";
    const border = isDarkMode ? "border-gray-700" : "border-gray-100";
    const inputClass = `px-3 py-2 text-sm rounded-lg border focus:outline-none focus:ring-2 focus:ring-primary ${
        isDarkMode ? "bg-gray-900 border-gray-700 text-gray-100" : "bg-white border-gray-200 text-gray-900"
    }`;

    const productRow = (p, indent = false) => (
        <div key={`${p.product_id}-${p.variant_id}`}
             className={`flex items-center justify-between gap-3 py-2.5 ${indent ? "pl-8 pr-4 sm:pl-10 sm:pr-6" : "px-4 sm:px-6"}`}>
            <div className="min-w-0">
                <p className={`text-sm font-medium truncate ${strong}`}>{p.name}</p>
                <p className={`text-xs ${muted}`}>
                    {!groupByCategory && <span>{p.category_name} · </span>}
                    {t("employeeDetails.soldProducts.salesCount", {count: p.sales_count})}
                </p>
            </div>
            <div className="text-right flex-shrink-0">
                <p className={`text-sm font-semibold ${strong}`}>
                    {t("employeeDetails.soldProducts.qty", {qty: formatQty(p.quantity)})}
                </p>
                <p className={`text-xs ${muted}`}>{formatCurrency(p.revenue)} DH</p>
            </div>
        </div>
    );

    return (
        // Edge-to-edge block, same title bar as the other EmployeeDetails sections
        <section className={`border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"} ${card}`}>
            <div className={`px-4 sm:px-6 py-2.5 border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}>
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                    <div className="flex items-center gap-2">
                        <Package className="w-4 h-4 text-primary flex-shrink-0"/>
                        <div>
                            <h2 className={`text-xs font-semibold uppercase tracking-wider ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
                                {t("employeeDetails.soldProducts.title")}
                            </h2>
                            {totals && (
                                <p className={`text-xs ${muted}`}>
                                    {t("employeeDetails.soldProducts.summary", {
                                        products: totals.products_count,
                                        qty: formatQty(totals.quantity),
                                        amount: formatCurrency(totals.revenue),
                                    })}
                                </p>
                            )}
                        </div>
                        {isFetching && !isLoading && <Loader className="w-4 h-4 text-primary animate-spin"/>}
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                        <div className="relative">
                            <Search className={`w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 ${muted}`}/>
                            <input
                                type="search"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder={t("employeeDetails.soldProducts.search")}
                                className={`${inputClass} pl-8 w-44 sm:w-56`}
                            />
                        </div>
                        <AppleSelect value={sortBy} onChange={(e) => setSortBy(e.target.value)} className={inputClass}>
                            <option value="quantity">{t("employeeDetails.soldProducts.sortQuantity")}</option>
                            <option value="revenue">{t("employeeDetails.soldProducts.sortRevenue")}</option>
                            <option value="name">{t("employeeDetails.soldProducts.sortName")}</option>
                        </AppleSelect>
                        <div className={`inline-flex rounded-lg border overflow-hidden ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}>
                            {[
                                {value: false, label: t("employeeDetails.soldProducts.byProduct"), Icon: Package},
                                {value: true, label: t("employeeDetails.soldProducts.byCategory"), Icon: Layers},
                            ].map(({value, label, Icon}) => (
                                <button
                                    key={String(value)}
                                    type="button"
                                    onClick={() => setGroupByCategory(value)}
                                    className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors ${
                                        groupByCategory === value
                                            ? "bg-primary text-white"
                                            : isDarkMode ? "text-gray-300 hover:bg-gray-700" : "text-gray-700 hover:bg-gray-50"
                                    }`}
                                >
                                    <Icon className="w-4 h-4"/>{label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {isLoading ? (
                <div className="flex justify-center items-center py-12">
                    <Loader className="w-6 h-6 text-primary animate-spin"/>
                </div>
            ) : isError ? (
                <p className="text-sm text-red-500 text-center py-10">{t("employeeDetails.soldProducts.fetchError")}</p>
            ) : products.length === 0 ? (
                <p className={`text-sm text-center py-10 ${muted}`}>
                    {search ? t("employeeDetails.soldProducts.noMatch") : t("employeeDetails.soldProducts.empty")}
                </p>
            ) : groupByCategory ? (
                <div className={`divide-y ${isDarkMode ? "divide-gray-700" : "divide-gray-100"}`}>
                    {categories.map((cat) => {
                        const isCollapsed = !!collapsed[cat.key];
                        return (
                            <div key={cat.key}>
                                <button
                                    type="button"
                                    onClick={() => setCollapsed((prev) => ({...prev, [cat.key]: !prev[cat.key]}))}
                                    className={`w-full flex items-center justify-between gap-3 px-4 sm:px-6 py-3 text-left ${isDarkMode ? "hover:bg-gray-700/40" : "hover:bg-gray-50"}`}
                                >
                                    <span className="flex items-center gap-2 min-w-0">
                                        {isCollapsed
                                            ? <ChevronRight className={`w-4 h-4 flex-shrink-0 ${muted}`}/>
                                            : <ChevronDown className={`w-4 h-4 flex-shrink-0 ${muted}`}/>}
                                        <span className={`text-sm font-semibold truncate ${strong}`}>{cat.name}</span>
                                        <span className={`text-xs flex-shrink-0 ${muted}`}>
                                            {t("employeeDetails.soldProducts.productsCount", {count: cat.products.length})}
                                        </span>
                                    </span>
                                    <span className="text-right flex-shrink-0">
                                        <span className={`block text-sm font-bold ${strong}`}>
                                            {t("employeeDetails.soldProducts.qty", {qty: formatQty(cat.quantity)})}
                                        </span>
                                        <span className={`block text-xs ${muted}`}>{formatCurrency(cat.revenue)} DH</span>
                                    </span>
                                </button>
                                {!isCollapsed && (
                                    <div className={`divide-y border-t ${border} ${isDarkMode ? "divide-gray-700/60 bg-gray-900/30" : "divide-gray-50 bg-gray-50/50"}`}>
                                        {cat.products.map((p) => productRow(p, true))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className={`divide-y ${isDarkMode ? "divide-gray-700" : "divide-gray-100"}`}>
                    {products.map((p) => productRow(p))}
                </div>
            )}
        </section>
    );
};

export default EmployeeSoldProducts;
